// Tenant Configuration
// This file centralizes all tenant-specific settings loaded from environment variables

import i18n from '@/i18n';

export type TenantCode = 'TOCA' | 'SGS' | 'DEMO';

export interface TenantConfig {
  // Identity
  code: TenantCode;
  name: string;
  title: string;

  // API
  apiUrl: string;

  // Branding
  logoPath: string;
  primaryColor: string;

  // Features
  features: {
    requisitions: boolean;
    recovery: boolean;
    damage: boolean;
    repair: boolean;
    reports: boolean;
    clients: boolean;
    trends: boolean;
    advancedDashboard: boolean;
    userClientAssociation: boolean;
    maintenance: boolean;
    productionOperations: boolean;
    billing: boolean;
  };

  // Asset statuses (differ between tenants)
  assetStatuses: Array<{
    value: number | string;
    label: string;
    color: string;
  }>;

  // Location types (Toca only)
  locationTypes?: Array<{
    value: string;
    label: string;
  }>;
}

// Parse boolean from env string
const parseEnvBool = (value: string | undefined, defaultValue = false): boolean => {
  if (value === undefined) return defaultValue;
  return value.toLowerCase() === 'true';
};

// Get tenant code from environment
const getTenantCode = (): TenantCode => {
  const code = import.meta.env.VITE_TENANT_CODE?.toUpperCase();
  if (code === 'SGS' || code === 'TOCA' || code === 'DEMO') {
    return code;
  }
  return 'SGS'; // Default to asset industry
};

// SGS-specific asset statuses (values match backend AssetStatus enum: Ok=1, Location=2, Incident=3, Pending=4)
const sgsAssetStatuses = [
  { value: 1, label: 'OK', color: 'bg-green-100 text-green-700' },
  { value: 4, label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  { value: 2, label: 'Discrepancia de Ubicación', color: 'bg-orange-100 text-orange-700' },
  { value: 3, label: 'Incidente', color: 'bg-red-100 text-red-700' },
];

// Toca-specific asset statuses
const tocaAssetStatuses = [
  { value: 1, label: 'Registered', color: 'bg-blue-100 text-blue-700' },
  { value: 2, label: 'Delivered', color: 'bg-purple-100 text-purple-700' },
  { value: 3, label: 'Recovered', color: 'bg-green-100 text-green-700' },
  { value: 4, label: 'In Transit', color: 'bg-yellow-100 text-yellow-700' },
  { value: 5, label: 'Damaged', color: 'bg-red-100 text-red-700' },
  { value: 6, label: 'Available', color: 'bg-emerald-100 text-emerald-700' },
];

// Toca location types
const tocaLocationTypes = [
  { value: 'BACKOFFICE', label: 'Warehouse' },
  { value: 'CLIENT', label: 'Client Site' },
  { value: 'TRANSFER', label: 'Transfer Point' },
];

// Build tenant configuration from environment
const buildTenantConfig = (): TenantConfig => {
  const code = getTenantCode();
  const isToca = code === 'TOCA';

  return {
    // Identity
    code,
    name: import.meta.env.VITE_APP_NAME || (isToca ? 'Toca Pallets' : 'SGS Asset Control'),
    title: import.meta.env.VITE_APP_TITLE || (isToca ? 'Toca Pallets - Control de Tarimas' : 'SGS - Control de Activos'),

    // API
    apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:5000',

    // Branding
    logoPath: `/assets/logos/${code.toLowerCase()}/logo.png`,
    primaryColor: isToca ? '#384BD3' : '#16a34a', // Blue for Toca, Green for SGS

    // Features (from environment or defaults based on tenant)
    features: {
      requisitions: parseEnvBool(import.meta.env.VITE_FEATURE_REQUISITIONS, isToca),
      recovery: parseEnvBool(import.meta.env.VITE_FEATURE_RECOVERY, isToca),
      damage: parseEnvBool(import.meta.env.VITE_FEATURE_DAMAGE, isToca),
      repair: parseEnvBool(import.meta.env.VITE_FEATURE_REPAIR, isToca),
      reports: parseEnvBool(import.meta.env.VITE_FEATURE_REPORTS, isToca),
      clients: parseEnvBool(import.meta.env.VITE_FEATURE_CLIENTS, isToca),
      trends: parseEnvBool(import.meta.env.VITE_FEATURE_TRENDS, isToca),
      advancedDashboard: parseEnvBool(import.meta.env.VITE_FEATURE_ADVANCED_DASHBOARD, isToca),
      userClientAssociation: parseEnvBool(import.meta.env.VITE_FEATURE_USER_CLIENT_ASSOCIATION, isToca),
      maintenance: parseEnvBool(import.meta.env.VITE_FEATURE_MAINTENANCE, false),
      productionOperations: parseEnvBool(import.meta.env.VITE_FEATURE_PRODUCTION_OPERATIONS, false),
      // Billing is TOCA-only in v1. SGS/DEMO stays off by default.
      billing: parseEnvBool(import.meta.env.VITE_FEATURE_BILLING, isToca),
    },

    // Asset statuses
    assetStatuses: isToca ? tocaAssetStatuses : sgsAssetStatuses,

    // Location types (Toca only)
    locationTypes: isToca ? tocaLocationTypes : undefined,
  };
};

// Export singleton tenant config
export const tenantConfig = buildTenantConfig();

// Helper hooks and functions
export const useTenantConfig = () => tenantConfig;

export const isTocaTenant = () => tenantConfig.code === 'TOCA';
export const isSGSTenant = () => tenantConfig.code === 'SGS' || tenantConfig.code === 'DEMO';
export const isDemoTenant = () => tenantConfig.code === 'DEMO';

/**
 * Resolve the tenant/entity code from the current hostname for SaaS subdomain
 * routing — e.g. `blackblock.activeid.app` → `"BLACKBLOCK"`. One UI + one API
 * serve every tenant; the subdomain picks the tenant at runtime (no per-tenant
 * build). Returns `null` for hosts that don't encode a tenant (localhost, IPs,
 * the bare apex, or the raw CloudFront domain) so the caller falls back to the
 * manual Organization field.
 */
export const resolveEntityFromHostname = (hostname?: string): string | null => {
  const host = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase();
  // ONLY the SaaS domain uses subdomain→tenant routing. Every other host —
  // custom/vanity domains (e.g. *.tocapallets.com.mx), localhost, IPs, the raw
  // CloudFront domain, the apex — keeps the build-time VITE_TENANT_CODE. Without
  // this scoping, app.tocapallets.com.mx would wrongly resolve to "APP".
  const SAAS_SUFFIX = '.activeid.app';
  if (!host.endsWith(SAAS_SUFFIX)) return null;
  const sub = host.slice(0, -SAAS_SUFFIX.length);
  if (!sub || sub.includes('.') || sub === 'www') return null; // single-level only
  return sub.toUpperCase();
};

export const getAssetStatusLabel = (status: number | string): string => {
  const found = tenantConfig.assetStatuses.find((s) => s.value === status);
  // i18n is the source of truth for the label (per-tenant + per-language via
  // common.statuses.asset.<code> overrides). The hardcoded config label is the
  // fallback for codes not present in the catalog. Called at render time so the
  // label tracks the active language.
  return i18n.t(`common:statuses.asset.${status}`, {
    defaultValue: found?.label || String(status),
  });
};

export const getAssetStatusColor = (status: number | string): string => {
  const found = tenantConfig.assetStatuses.find((s) => s.value === status);
  return found?.color || 'bg-gray-100 text-gray-700';
};

export const getLocationTypeLabel = (type: string): string => {
  if (!tenantConfig.locationTypes) return type;
  const found = tenantConfig.locationTypes.find((t) => t.value === type);
  return found?.label || type;
};
